import React, {useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import styled from '@emotion/styled'
import {Review} from '@findhotel/sapi'
import {SelectedElementType} from 'components/Accommodation/ReviewsSection'
import {ReviewCommentWrapper} from 'components/Reviews/ReviewCommentWrapper'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'

import {Button} from '@vio/atlas/Button'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {getLengthOfStay} from '@vio/core/src/utils/date'
import {
  LoadMoreReviews,
  ReviewSource
} from '@vio/shared/src/search/ReviewComment'

import {ReviewsLoadingList} from '../ReviewLoading'
import {reviewMessages} from '../ReviewMessages'

interface Props {
  reviewsData: Review[]
  noMoreReviews: boolean
  mountedOnOverlay?: boolean
  onLoadMore: () => void
  sourceComponent: string
  searchKeywords?: string[]
  reviewsAvailable?: number
  translatedReviews?: Record<string, string>
  isTranslatingReviews?: boolean
}
const ReviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s500};
  ${linariaMq.desktopXs} {
    column-gap: ${cssTheme.layout.spacing.s900};
  }
`

export const GuestReviews: React.FC<Props> = ({
  reviewsData,
  noMoreReviews,
  mountedOnOverlay = false,
  onLoadMore,
  sourceComponent,
  searchKeywords,
  reviewsAvailable,
  translatedReviews = {},
  isTranslatingReviews = false
}) => {
  const {formatMessage} = useIntl()
  const dispatch = useDispatch()
  const showOverlay = useShowOverlay()

  const handleReadAllReviews = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.ReviewDetails,
        action: Action.Clicked,
        payload: {
          selectedElement: SelectedElementType.ReadAllReviews
        }
      })
    )

    showOverlay({
      type: OverlayType.GuestReviews
    })
  }, [dispatch, showOverlay])

  const onSourceLogoClick = () => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.ReviewDetails,
        action: Action.Clicked,
        payload: {
          selectedElement: SelectedElementType.ReviewLogo
        }
      })
    )
  }

  const showReadAllReviewsButton = useMemo(() => {
    if (mountedOnOverlay) return false
    return reviewsData?.length > 4
  }, [mountedOnOverlay, reviewsData])

  const reviews = useMemo(() => {
    if (showReadAllReviewsButton || !mountedOnOverlay) {
      return reviewsData.slice(0, 5)
    }
    return reviewsData
  }, [showReadAllReviewsButton, reviewsData, mountedOnOverlay])

  if (isTranslatingReviews) return <ReviewsLoadingList />

  return (
    <>
      {searchKeywords?.length > 0 && reviewsAvailable && (
        <Box paddingBottom="s500">
          <Text variant="titleM">
            {formatMessage(reviewMessages.reviewSectionCount, {
              count: reviewsAvailable
            })}
          </Text>
        </Box>
      )}
      <ReviewWrapper>
        {reviews.map((review, index) => (
          <React.Fragment key={[review.reviewDate, index].join('-')}>
            {index > 0 && <Divider />}
            <ReviewCommentWrapper
              reviewText={review.text}
              reviewLanguage={review.language}
              reviewerScore={review.reviewerScore}
              reviewDate={review.reviewDate}
              lengthOfStay={getLengthOfStay(review.checkIn, review.checkOut)}
              reviewerName={review.reviewerName}
              onSourceLogoClick={onSourceLogoClick}
              SourceLogoSlot={
                review?.reviewSource && (
                  <ReviewSource providerCode={review.reviewSource} />
                )
              }
              reviewSource={review.reviewSource}
              truncatedContentLines={mountedOnOverlay ? 5 : 3}
              searchKeywords={searchKeywords}
              translatedReviewText={translatedReviews?.[review?.id]}
            />
          </React.Fragment>
        ))}

        {mountedOnOverlay && (
          <LoadMoreReviews
            noMoreReviews={noMoreReviews}
            onLoadMore={onLoadMore}
            sourceComponent={sourceComponent}
          />
        )}

        {mountedOnOverlay && <Box paddingBottom="s700" />}

        {showReadAllReviewsButton && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Box paddingTop="s300">
              <Button
                variant="tertiary"
                size="xl"
                onClick={handleReadAllReviews}
              >
                {formatMessage(reviewMessages.readAllReviews)}
              </Button>
            </Box>
          </Box>
        )}
      </ReviewWrapper>
    </>
  )
}
